import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment";
import Table from "../../../core/common/dataTable";
import { getDarList } from "../../../core/data/redux/darSlice";
import { AppDispatch } from "../../../core/data/redux/store";
import { exportToPDF } from "../../../utils/exportToPDF";
import { exportToExcel } from "../../../utils/exportToExcel";

const columns = [
  { 
    title: "Case No", 
    dataIndex: "caseNo", 
    key: "caseNo",
    render: (text: string) => <span className="fw-bold text-dark">{text}</span>
  },
  { 
    title: "Employee No", 
    dataIndex: "empNo", 
    key: "empNo",
    render: (text: string) => <span className="fw-medium text-primary">{text || "-"}</span>
  },
  { 
    title: "Employee Name", 
    dataIndex: "empName", 
    key: "empName" 
  },
  { 
    title: "Designation", 
    dataIndex: "designation", 
    key: "designation" 
  },
  { 
    title: "Case Type", 
    dataIndex: "caseType", 
    key: "caseType" 
  },
  { 
    title: "DA Name", 
    dataIndex: "daName", 
    key: "daName" 
  },
  {
    title: "Created Date",
    dataIndex: "createdDate",
    key: "createdDate",
    render: (date: string) =>
      date ? (moment(date).isValid() ? moment(date).format("DD MMM YYYY") : String(date)) : "-"
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (status: string) => {
      const s = (status || "").toUpperCase();
      const isPending = s.includes("PENDING");
      const isClosed = s.includes("CLOSED");
      const isAppeal = s.includes("APPEAL");
      const isSuspension = s.includes("SUSPENSION") || s.includes("SUSPENDED");

      let badgeClass = "bg-info-transparent text-info border border-info";
      let icon = "ti-file-pencil";

      if (isClosed) {
        badgeClass = "bg-success-transparent text-success border border-success";
        icon = "ti-circle-check";
      } else if (isPending) {
        badgeClass = "bg-warning-transparent text-warning border border-warning";
        icon = "ti-clock";
      } else if (isAppeal) {
        badgeClass = "bg-primary-transparent text-primary border border-primary";
        icon = "ti-gavel";
      } else if (isSuspension) {
        badgeClass = "bg-danger-transparent text-danger border border-danger";
        icon = "ti-alert-circle";
      }

      return (
        <span className={`badge ${badgeClass} rounded-pill fw-semibold px-3 py-1 d-inline-flex align-items-center gap-1`} style={{ fontSize: '0.75rem' }}>
          <i className={`ti ${icon} fs-6`}></i> {status || "UNKNOWN"}
        </span>
      );
    },
  },
  {
    title: "Actions",
    key: "actions",
    render: (text: string, record: any) => (
      <Link to={`/dar-cases/details/${record.caseNo}`} className="btn btn-sm btn-light border d-inline-flex align-items-center gap-1 shadow-sm transition-all hover-primary">
        <i className="ti ti-eye text-primary"></i> View
      </Link>
    ),
  },
];

const DarDashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [empNoFilter, setEmpNoFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [stats, setStats] = useState({ total: 0, contemplated: 0, pending: 0, closed: 0 });

  const fetchCases = async (
    page = 1,
    search = searchTerm,
    empFilter = empNoFilter,
    status = statusFilter
  ) => {
    setLoading(true);
    try {
      const activeSearch = search.trim() || empFilter.trim();
      const filtersPayload: any = {};
      if (activeSearch) {
        filtersPayload.search_term = activeSearch;
      }

      const response = await dispatch(getDarList({
        page,
        limit: 2000,
        filters: filtersPayload
      })).unwrap();

      const statusResp = response?.status || response?.data?.status;
      if (statusResp === 200 || statusResp === 201 || response?.data) {
        const rawData = response?.data?.items || response?.data?.data?.items || response?.data?.data || response?.data || [];
        const items = Array.isArray(rawData) ? rawData : (rawData.items || []);

        // Deduplicate cases to show ONLY the current / latest record per case
        const deduplicatedMap = new Map<string, any>();
        items.forEach((item: any, idx: number) => {
          if (!item) return;
          const vType = String(item.version_type || item.versionType || '').toLowerCase();
          if (vType === 'previous' || item.is_previous === true || item.isPrevious === true) return;

          const cNo = String(item.caseno || item.case_no || item.caseNo || '').trim();
          let baseCaseNo = cNo;
          let versionNum = 0;
          if (cNo && cNo !== '-') {
            const parts = cNo.split('-');
            if (parts.length >= 4 && /^\d+$/.test(parts[parts.length - 1])) {
              baseCaseNo = parts.slice(0, -1).join('-');
              versionNum = parseInt(parts[parts.length - 1], 10) || 0;
            }
          }

          const key = baseCaseNo || `${item.empno || item.emp_no || Math.random()}_${item.id || idx}`;

          if (!deduplicatedMap.has(key)) {
            deduplicatedMap.set(key, item);
          } else {
            const existing = deduplicatedMap.get(key);
            const existingCNo = String(existing.caseno || existing.case_no || existing.caseNo || '').trim();
            let existingVersionNum = 0;
            if (existingCNo && existingCNo !== '-') {
              const parts = existingCNo.split('-');
              if (parts.length >= 4 && /^\d+$/.test(parts[parts.length - 1])) {
                existingVersionNum = parseInt(parts[parts.length - 1], 10) || 0;
              }
            }

            const existingTime = new Date(existing.updateddate || existing.updated_date || existing.createddate || existing.created_date || existing.created_at || 0).getTime();
            const currentTime = new Date(item.updateddate || item.updated_date || item.createddate || item.created_date || item.created_at || 0).getTime();
            const existingId = Number(existing.id || existing._id || 0);
            const currentId = Number(item.id || item._id || 0);

            if (
              versionNum > existingVersionNum ||
              (versionNum === existingVersionNum && currentTime > existingTime) ||
              (versionNum === existingVersionNum && currentTime === existingTime && currentId >= existingId)
            ) {
              deduplicatedMap.set(key, item);
            }
          }
        });

        const currentItems = Array.from(deduplicatedMap.values());

        if (currentItems) {
          const formattedData = currentItems.map((item: any, index: number) => ({
            key: item.caseno || item.case_no || item.caseNo || index.toString(),
            caseNo: item.caseno || item.case_no || item.caseNo || '-',
            empNo: item.empno || item.emp_no || item.employee_no || item.emp_id || item.empid || item.employeeNo || item.empId || item.target_empno || '-',
            empName: item.empname || item.emp_name || item.employee_name || item.name || item.employeeName || item.target_empname || '-',
            designation: item.designation || item.designation_name || '-',
            caseType: item.casetype || item.case_type || item.caseType || '-',
            daName: item.daname || item.da_name || item.daName || item.da_empname || '-',
            createdDate: item.createddate || item.created_date || item.created_at || item.createdAt || item.date || item.issue_date || null,
            status: item.status || '-',
            rawItem: item
          }));
          setData(formattedData);
          setPagination(prev => ({ ...prev, page, total: formattedData.length }));

          const isFiltered = empFilter.trim() || (status && status !== "ALL");
          setStats(prevStats => ({
            total: prevStats.total > 0 && isFiltered ? prevStats.total : formattedData.length,
            contemplated: prevStats.contemplated > 0 && isFiltered ? prevStats.contemplated : formattedData.filter((i: any) => String(i.status || '').toUpperCase().includes("CONTEMPLATED")).length,
            pending: prevStats.pending > 0 && isFiltered ? prevStats.pending : formattedData.filter((i: any) => {
              const s = String(i.status || '').toUpperCase();
              return s.includes("PENDING") && !s.includes("APPEAL") && !s.includes("CLOSED");
            }).length,
            closed: prevStats.closed > 0 && isFiltered ? prevStats.closed : formattedData.filter((i: any) => String(i.status || '').toUpperCase().includes("CLOSED")).length,
          }));
        }
      } else {
        toast.error(response?.message || "Failed to fetch cases");
      }
    } catch (err: any) {
      toast.error(err?.message || "Error fetching cases");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchCases(1, searchTerm, empNoFilter, statusFilter);
    }, 400);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, empNoFilter, statusFilter]);

  const filteredData = useMemo(() => {
    let result = data;

    if (searchTerm.trim()) {
      const lowerSearch = searchTerm.trim().toLowerCase();
      const cleanSearch = lowerSearch.replace(/^0+/, '');
      result = result.filter(item => {
        const rawEmpNo = String(item.empNo || '').trim().toLowerCase();
        const cleanEmpNo = rawEmpNo.replace(/^0+/, '');
        const empName = String(item.empName || '').trim().toLowerCase();
        const caseNo = String(item.caseNo || '').trim().toLowerCase();
        const designation = String(item.designation || '').trim().toLowerCase();
        const caseType = String(item.caseType || '').trim().toLowerCase();
        const daName = String(item.daName || '').trim().toLowerCase();
        const status = String(item.status || '').trim().toLowerCase();

        return (
          rawEmpNo.includes(lowerSearch) ||
          (cleanSearch.length > 0 && cleanEmpNo.includes(cleanSearch)) ||
          empName.includes(lowerSearch) ||
          caseNo.includes(lowerSearch) ||
          designation.includes(lowerSearch) ||
          caseType.includes(lowerSearch) ||
          daName.includes(lowerSearch) ||
          status.includes(lowerSearch)
        );
      });
    }

    if (empNoFilter.trim()) {
      const lowerFilter = empNoFilter.trim().toLowerCase();
      const cleanFilter = lowerFilter.replace(/^0+/, '');
      result = result.filter(item => {
        const rawEmpNo = String(item.empNo || item.rawItem?.empno || item.rawItem?.emp_no || '').trim().toLowerCase();
        const cleanEmpNo = rawEmpNo.replace(/^0+/, '');
        const empName = String(item.empName || item.rawItem?.empname || '').trim().toLowerCase();
        const caseNo = String(item.caseNo || item.rawItem?.caseno || '').trim().toLowerCase();
        const daName = String(item.daName || item.rawItem?.daname || '').trim().toLowerCase();

        const matchEmpNo =
          (rawEmpNo.length > 0 && rawEmpNo.includes(lowerFilter)) ||
          (cleanFilter.length > 0 && cleanEmpNo.includes(cleanFilter)) ||
          (cleanEmpNo.length > 0 && lowerFilter.includes(cleanEmpNo));
        const matchName = empName.includes(lowerFilter);
        const matchCaseNo = caseNo.includes(lowerFilter);
        const matchDa = daName.includes(lowerFilter);

        return matchEmpNo || matchName || matchCaseNo || matchDa;
      });
    }

    if (statusFilter && statusFilter !== "ALL" && statusFilter !== "All Statuses") {
      const target = statusFilter.trim().toUpperCase();
      result = result.filter(item => {
        const itemStatus = String(item.status || '').trim().toUpperCase();
        if (target === "CLOSED") {
          return itemStatus.includes("CLOSED");
        }
        if (target === "APPEAL") {
          return itemStatus.includes("APPEAL");
        }
        if (target === "APPEAL_CLOSED") {
          return itemStatus.includes("APPEAL CLOSED");
        }
        if (target === "SUSPENSION") {
          return itemStatus.includes("SUSPENSION") || itemStatus.includes("SUSPENDED") || itemStatus.includes("ACTIVE");
        }
        if (target === "PENDING") {
          return itemStatus.includes("PENDING") && !itemStatus.includes("APPEAL CLOSED");
        }
        if (target === "CONTEMPLATED") {
          return itemStatus.includes("CONTEMPLATED") || itemStatus.includes("CONTEMPLATE");
        }
        return itemStatus.includes(target);
      });
    }

    return result;
  }, [data, searchTerm, empNoFilter, statusFilter]);

  const handleExportPDF = () => {
    const pdfColumns = [
      { title: "Case No", dataIndex: "caseNo" },
      { title: "Employee No", dataIndex: "empNo" },
      { title: "Employee Name", dataIndex: "empName" },
      { title: "Designation", dataIndex: "designation" },
      { title: "Case Type", dataIndex: "caseType" },
      { title: "DA Name", dataIndex: "daName" },
      { title: "Created Date", dataIndex: "createdDate", render: (date: string) => date ? moment(date).format("DD MMM YYYY") : "-" },
      { title: "Status", dataIndex: "status" }
    ];
    exportToPDF({
      title: "D&AR Dashboard Cases Report",
      columns: pdfColumns,
      data: filteredData,
      fileName: "DMRC_DAR_Dashboard_Cases"
    });
  };

  const handleExportExcel = () => {
    const excelColumns = [
      { title: "Case No", dataIndex: "caseNo" },
      { title: "Employee No", dataIndex: "empNo" },
      { title: "Employee Name", dataIndex: "empName" },
      { title: "Designation", dataIndex: "designation" },
      { title: "Case Type", dataIndex: "caseType" },
      { title: "DA Name", dataIndex: "daName" },
      { title: "Created Date", dataIndex: "createdDate", render: (date: string) => date ? moment(date).format("DD MMM YYYY") : "-" },
      { title: "Status", dataIndex: "status" }
    ];
    exportToExcel({
      title: "D&AR Dashboard Cases Report",
      columns: excelColumns,
      data: filteredData,
      fileName: "DMRC_DAR_Dashboard_Cases"
    });
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <style dangerouslySetInnerHTML={{__html: `
          .custom-stats-card {
            transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
            cursor: pointer;
            border: 1px solid rgba(226, 232, 240, 0.8) !important;
          }
          .custom-stats-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -4px rgba(0, 0, 0, 0.08) !important;
          }
          .custom-stats-card.active-card {
            border-width: 2px !important;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12) !important;
          }
          /* Table Styling Overrides */
          .ant-table-thead > tr > th {
            background-color: #f8fafc !important;
            color: #475569 !important;
            font-weight: 600 !important;
            font-size: 13px !important;
            border-bottom: 1px solid #e2e8f0 !important;
            padding: 12px 16px !important;
          }
          .ant-table-tbody > tr > td {
            font-size: 13px !important;
            color: #334155 !important;
            padding: 12px 16px !important;
          }
          .ant-table-tbody > tr:hover > td {
            background-color: #f8fafc !important;
          }
        `}} />

        {/* PAGE HEADER */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2 mb-md-0">
            <h4 className="page-title mb-0 fw-bold text-dark d-flex align-items-center gap-2 fs-18">
              <i className="ti ti-briefcase text-danger"></i> D&AR Dashboard
            </h4>
            <p className="text-muted mb-0 fs-12">Manage and track all Disciplinary & Appeal Rules cases efficiently.</p>
          </div>
          <div className="d-flex gap-2">
            <Link to="/dar-cases/new" className="btn btn-danger d-flex align-items-center gap-2 shadow-sm text-white">
              <i className="ti ti-plus fs-5"></i> Initiate New Case
            </Link>
          </div>
        </div>

        {/* STATS CARDS */}
        <div className="row g-2.5 mb-3">
          <div className="col-12 col-sm-6 col-xl-3" onClick={() => setStatusFilter("ALL")}>
            <div className={`card shadow-none rounded-lg custom-stats-card ${statusFilter === "ALL" ? 'active-card border-primary' : ''}`} style={{ borderLeft: '4px solid #4f46e5', background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.05) 0%, rgba(255, 255, 255, 1) 100%)' }}>
              <div className="card-body py-2 px-2.5 d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <i className="ti ti-folders fs-14" style={{ color: '#4f46e5' }}></i>
                  <span className="text-muted text-uppercase fw-bold" style={{ fontSize: '10.5px', letterSpacing: '0.5px' }}>Total Cases</span>
                </div>
                <h5 className="mb-0 fw-bold text-dark fs-15">{stats.total}</h5>
              </div>
            </div>
          </div>

          <div className="col-12 col-sm-6 col-xl-3" onClick={() => setStatusFilter("CONTEMPLATED")}>
            <div className={`card shadow-none rounded-lg custom-stats-card ${statusFilter === "CONTEMPLATED" ? 'active-card border-info' : ''}`} style={{ borderLeft: '4px solid #06b6d4', background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.05) 0%, rgba(255, 255, 255, 1) 100%)' }}>
              <div className="card-body py-2 px-2.5 d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <i className="ti ti-file-pencil fs-14" style={{ color: '#06b6d4' }}></i>
                  <span className="text-muted text-uppercase fw-bold" style={{ fontSize: '10.5px', letterSpacing: '0.5px' }}>Contemplated</span>
                </div>
                <h5 className="mb-0 fw-bold text-dark fs-15">{stats.contemplated}</h5>
              </div>
            </div>
          </div>

          <div className="col-12 col-sm-6 col-xl-3" onClick={() => setStatusFilter("PENDING")}>
            <div className={`card shadow-none rounded-lg custom-stats-card ${statusFilter === "PENDING" ? 'active-card border-warning' : ''}`} style={{ borderLeft: '4px solid #f59e0b', background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.05) 0%, rgba(255, 255, 255, 1) 100%)' }}>
              <div className="card-body py-2 px-2.5 d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <i className="ti ti-clock fs-14" style={{ color: '#f59e0b' }}></i>
                  <span className="text-muted text-uppercase fw-bold" style={{ fontSize: '10.5px', letterSpacing: '0.5px' }}>Pending</span>
                </div>
                <h5 className="mb-0 fw-bold text-dark fs-15">{stats.pending}</h5>
              </div>
            </div>
          </div>

          <div className="col-12 col-sm-6 col-xl-3" onClick={() => setStatusFilter("CLOSED")}>
            <div className={`card shadow-none rounded-lg custom-stats-card ${statusFilter === "CLOSED" ? 'active-card border-success' : ''}`} style={{ borderLeft: '4px solid #10b981', background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(255, 255, 255, 1) 100%)' }}>
              <div className="card-body py-2 px-2.5 d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <i className="ti ti-circle-check fs-14" style={{ color: '#10b981' }}></i>
                  <span className="text-muted text-uppercase fw-bold" style={{ fontSize: '10.5px', letterSpacing: '0.5px' }}>Closed</span>
                </div>
                <h5 className="mb-0 fw-bold text-dark fs-15">{stats.closed}</h5>
              </div>
            </div>
          </div>
        </div>

        {/* TABLE CARD */}
        <div className="card shadow-sm border-0 rounded-xl overflow-hidden">
          <div className="card-header bg-white border-bottom p-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h5 className="mb-0 fw-bold text-dark">Disciplinary & Appeal Cases</h5>
            
            {/* FILTERS & EXPORT CONTROLS JUST ABOVE TABLE */}
            <div className="d-flex align-items-center flex-wrap gap-2">
              {/* Employee No Filter */}
              <div className="input-group input-group-sm" style={{ width: '220px' }}>
                <span className="input-group-text bg-light border-end-0 text-muted">
                  <i className="ti ti-filter"></i>
                </span>
                <input
                  type="text"
                  className="form-control border-start-0 ps-0"
                  placeholder="Filter by Emp No..."
                  value={empNoFilter}
                  onChange={(e) => setEmpNoFilter(e.target.value)}
                />
              </div>

              {/* Status Filter */}
              <select
                className="form-select form-select-sm"
                style={{ width: '170px' }}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="ALL">All Statuses</option>
                <option value="CONTEMPLATED">Contemplated</option>
                <option value="PENDING">Pending</option>
                <option value="CLOSED">Closed</option>
                <option value="APPEAL">Appeal (All)</option>
                <option value="APPEAL_CLOSED">Appeal Closed</option>
                <option value="SUSPENSION">Suspension / Active</option>
              </select>

              <span className="badge bg-light text-dark border px-3 py-2 rounded-pill shadow-none fw-medium ms-1">
                {filteredData.length} Records
              </span>

              {/* Export Excel */}
              <button
                type="button"
                onClick={handleExportExcel}
                className="btn btn-sm btn-outline-success d-flex align-items-center shadow-sm ms-1"
                title="Export Table to Excel"
              >
                <i className="ti ti-file-spreadsheet me-1 fs-5"></i>
                Export Excel
              </button>

              {/* Export PDF */}
              <button
                type="button"
                onClick={handleExportPDF}
                className="btn btn-sm btn-outline-danger d-flex align-items-center shadow-sm"
                title="Export Table to PDF"
              >
                <i className="ti ti-file-type-pdf me-1 fs-5"></i>
                Export PDF
              </button>
            </div>
          </div>

          <div className="card-body p-0">
            <Table
              columns={columns}
              dataSource={filteredData}
              loading={loading}
              paginationConfig={{ pageSize: 10, total: filteredData.length }}
              handleSearch={(val: string) => setSearchTerm(val)}
              searchValue={searchTerm}
              searchPlaceholder="Search by Case No, Emp No, Name..."
            />
          </div>
        </div>

      </div>
    </div>
  );
};

export default DarDashboard;
